import {
  FEEDBACK_SCREENSHOT_ACCEPT,
  MAX_FEEDBACK_SCREENSHOT_BYTES,
  type FeedbackType,
} from "@estelle/domain";
import { CheckCircle2, ImagePlus } from "lucide-react";
import {
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from "react";

import { submitFeedback, FeedbackSubmitError } from "@/components/feedback/api";
import {
  NOT_AN_IMAGE,
  STILL_TOO_LARGE,
  canDecode,
  imageFrom,
  imageProblem,
  isFileDrag,
} from "@/components/feedback/attach";
import { useFeedbackContext } from "@/components/feedback/context";
import {
  ScreenshotCanvas,
  type ScreenshotCanvasHandle,
} from "@/components/feedback/ScreenshotCanvas";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: "bug", label: "Something's wrong" },
  { value: "confusing", label: "Confusing" },
  { value: "idea", label: "Idea" },
];

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The raw capture, before redaction. Null when capture failed outright. */
  screenshot: Blob | null;
}

/**
 * The feedback dialog (#319): the screenshot with blackout boxes, a type, a
 * note, and nothing else — everything in `FeedbackContext` is attached by
 * `useFeedbackContext()` without the reporter typing any of it.
 *
 * The picture is the automatic capture unless the reporter brings their own
 * (#498) — pasted from the clipboard, dropped on the dialog or chosen from a
 * file. It replaces the capture rather than joining it: a report has one
 * screenshot, stored in the one place, under the one limit. Their image goes
 * through the same canvas, so they can black out anything private in it just as
 * they can in the capture.
 *
 * `FeedbackButton` remounts this with a fresh `key` every time it is opened,
 * so a stale note or stale boxes from the last report never survive into
 * this one — the state below only ever needs to know its own starting value,
 * never to be reset by an effect reacting to a prop.
 */
export function FeedbackDialog({
  open,
  onOpenChange,
  screenshot,
}: FeedbackDialogProps) {
  const canvasRef = useRef<ScreenshotCanvasHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const buildContext = useFeedbackContext();

  const [type, setType] = useState<FeedbackType | null>(null);
  const [note, setNote] = useState("");
  // The reporter's own image, when they have attached one. It stands in for the
  // capture: `shown` is what the canvas draws and what is sent.
  const [own, setOwn] = useState<Blob | null>(null);
  const [ownProblem, setOwnProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(
    () => screenshot !== null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshotDropped, setScreenshotDropped] = useState(false);

  const shown = own ?? screenshot;

  async function attach(file: File) {
    const problem = imageProblem(file);
    if (problem) {
      setOwnProblem(problem);
      return;
    }
    // After the cheap checks, before anything is replaced: a file that cannot
    // be read as an image is refused here, and the picture already in the
    // dialog stays exactly as it was (#523).
    if (!(await canDecode(file))) {
      setOwnProblem(NOT_AN_IMAGE);
      return;
    }
    setOwnProblem(null);
    setOwn(file);
    setIncludeScreenshot(true);
    setScreenshotDropped(false);
  }

  function handlePaste(event: ClipboardEvent<HTMLElement>) {
    const file = imageFrom(event.clipboardData);
    // Only an image is taken. Text pasted into the note goes where it was
    // going.
    if (!file) return;
    event.preventDefault();
    void attach(file);
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    if (!isFileDrag(event.dataTransfer)) return;
    // Without this the browser would open the file instead of handing it over.
    event.preventDefault();
    setDragging(true);
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    setDragging(false);
    const file = imageFrom(event.dataTransfer);
    if (file) void attach(file);
    else setOwnProblem(NOT_AN_IMAGE);
  }

  async function resolveScreenshot(): Promise<Blob | null> {
    if (!includeScreenshot || !shown) return null;

    const png = await canvasRef.current?.flatten("image/png");
    if (png && png.size <= MAX_FEEDBACK_SCREENSHOT_BYTES) return png;

    // A capture that includes a busy, image-heavy screen can clear the cap
    // even redacted. Re-encoding as JPEG trades a little fidelity for a lot
    // of bytes before the last resort — sending the note without a picture —
    // per the decision that a note always beats a blocked submission.
    const jpeg = await canvasRef.current?.flatten("image/jpeg", 0.8);
    if (jpeg && jpeg.size <= MAX_FEEDBACK_SCREENSHOT_BYTES) return jpeg;

    // The one exception to that decision: an image the reporter chose on
    // purpose is not quietly left out. They are told, and decide.
    if (own) throw new FeedbackSubmitError(STILL_TOO_LARGE);

    setScreenshotDropped(true);
    return null;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!type || note.trim() === "" || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const finalScreenshot = await resolveScreenshot();
      await submitFeedback({
        type,
        note: note.trim(),
        context: buildContext(),
        screenshot: finalScreenshot,
      });
      setSent(true);
      // Auto-close rather than making someone dismiss a success message —
      // the whole point is a report sent in under 30 seconds.
      setTimeout(() => {
        onOpenChange(false);
      }, 1400);
    } catch (submitError) {
      setError(
        submitError instanceof FeedbackSubmitError
          ? submitError.message
          : "Could not send feedback. Try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Send feedback"
        description="A screenshot and a note go straight to the team."
        // On the panel rather than the form: focus starts in the header, and a
        // paste or a drop anywhere in the dialog should be taken, not only one
        // that lands on the picture.
        onPaste={handlePaste}
        onDragOver={handleDragOver}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={handleDrop}
      >
        {sent ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <CheckCircle2 className="text-primary size-8" aria-hidden />
            <p className="font-medium">Thanks — sent</p>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="flex flex-col gap-4"
          >
            <div
              className={cn(
                "flex flex-col gap-2 rounded-md",
                dragging && "ring-primary ring-2 ring-offset-2",
              )}
            >
              {shown ? (
                <>
                  {includeScreenshot ? (
                    <ScreenshotCanvas ref={canvasRef} screenshot={shown} />
                  ) : (
                    <p className="text-muted-foreground rounded-md border border-dashed px-3 py-6 text-center text-sm">
                      Screenshot will not be sent.
                    </p>
                  )}
                  <label className="flex w-fit items-center gap-2 text-sm">
                    <Checkbox
                      checked={includeScreenshot}
                      onCheckedChange={(checked) => {
                        setIncludeScreenshot(checked === true);
                      }}
                    />
                    Include screenshot
                  </label>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Couldn't capture a screenshot of this screen — attach your
                  own, or send your note without one.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FEEDBACK_SCREENSHOT_ACCEPT}
                  className="hidden"
                  aria-label="Choose an image to attach"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    // Reset so choosing the same file again still fires.
                    event.target.value = "";
                    if (file) void attach(file);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus aria-hidden />
                  {own ? "Choose a different image" : "Use my own image"}
                </Button>
                {own && screenshot ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    disabled={submitting}
                    onClick={() => {
                      setOwn(null);
                      setOwnProblem(null);
                      setIncludeScreenshot(true);
                    }}
                  >
                    Use the automatic screenshot
                  </Button>
                ) : null}
                <span className="text-muted-foreground text-xs">
                  or paste or drop a PNG or JPEG here
                </span>
              </div>
              {ownProblem ? (
                <p role="alert" className="text-destructive text-xs">
                  {ownProblem}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">What's this about?</span>
              <RadioGroup
                value={type ?? undefined}
                onValueChange={(value) => {
                  setType(value as FeedbackType);
                }}
                className="grid-cols-1 gap-2 sm:grid-cols-3"
              >
                {TYPE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      "hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm",
                      type === option.value && "border-primary bg-accent",
                    )}
                  >
                    <RadioGroupItem value={option.value} />
                    {option.label}
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="feedback-note">What happened?</Label>
              <Textarea
                id="feedback-note"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="Tell us what you saw"
                rows={4}
                maxLength={4000}
                required
              />
            </div>

            {screenshotDropped ? (
              <p className="text-muted-foreground text-xs">
                The screenshot was too large to send and was left out; your note
                was sent on its own.
              </p>
            ) : null}
            {error ? <p className="text-destructive text-sm">{error}</p> : null}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !type || note.trim() === ""}
              >
                {submitting ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
